import React, { useEffect, useState } from 'react';
import {
    ApiResponse, Usuario, RolUsuario, ROL_LABELS,
    ModuloSistema, MODULOS_SISTEMA, MODULO_LABELS
} from '../types';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/apiFetch';
import { toast } from '../lib/toast';

interface Props {
    onClose: () => void;
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

interface NuevoUsuarioForm {
    nombre: string;
    email: string;
    password: string;
    rol: RolUsuario;
    permisos_modulos: ModuloSistema[];
}

const formVacio: NuevoUsuarioForm = {
    nombre: '', email: '', password: '', rol: 'operador',
    permisos_modulos: ['kanban', 'clientes', 'documentos', 'audios']
};

const ToggleModulo: React.FC<{
    modulo: ModuloSistema;
    activo: boolean;
    onChange: (v: boolean) => void;
}> = ({ modulo, activo, onChange }) => (
    <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
        <input type="checkbox" checked={activo} onChange={e => onChange(e.target.checked)} className="rounded" />
        {MODULO_LABELS[modulo]}
    </label>
);

export const UsuariosPanel: React.FC<Props> = ({ onClose }) => {
    const { usuario: yo } = useAuth();
    const [usuarios, setUsuarios] = useState<Usuario[]>([]);
    const [loading, setLoading] = useState(true);
    const [mensaje, setMensaje] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);

    const [mostrarNuevo, setMostrarNuevo] = useState(false);
    const [nuevoForm, setNuevoForm] = useState<NuevoUsuarioForm>(formVacio);
    const [creando, setCreando] = useState(false);

    const [editandoId, setEditandoId] = useState<number | null>(null);
    const [editPermisos, setEditPermisos] = useState<ModuloSistema[]>([]);
    const [editRol, setEditRol] = useState<RolUsuario>('operador');
    const [guardandoId, setGuardandoId] = useState<number | null>(null);

    const [resetearId, setResetearId] = useState<number | null>(null);
    const [nuevaPassword, setNuevaPassword] = useState('');

    const cargar = async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`${API_BASE}/usuarios`);
            const json: ApiResponse<Usuario[]> = await res.json();
            if (json.success && json.data) setUsuarios(json.data);
        } catch (err) {
            console.error('Error al cargar usuarios:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { cargar(); }, []);

    const handleCrear = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!nuevoForm.nombre.trim() || !nuevoForm.email.trim() || nuevoForm.password.length < 6) return;

        setCreando(true);
        try {
            const res = await apiFetch(`${API_BASE}/usuarios`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(nuevoForm)
            });
            const json: ApiResponse<{ usuario: Usuario }> = await res.json();
            if (json.success) {
                setNuevoForm(formVacio);
                setMostrarNuevo(false);
                setMensaje({ tipo: 'ok', texto: 'Usuario creado correctamente.' });
                await cargar();
            } else {
                setMensaje({ tipo: 'error', texto: json.error || 'No se pudo crear el usuario.' });
            }
        } catch (err) {
            setMensaje({ tipo: 'error', texto: 'No se pudo conectar con el servidor.' });
        } finally {
            setCreando(false);
        }
    };

    const handleEditar = (u: Usuario) => {
        setEditandoId(u.id);
        setEditRol(u.rol);
        setEditPermisos(u.permisos_modulos);
        setMensaje(null);
    };

    const handleGuardarEdicion = async (id: number) => {
        setGuardandoId(id);
        try {
            const esUnoMismo = yo?.id === id;
            const body: any = { permisos_modulos: editPermisos };
            if (!esUnoMismo) body.rol = editRol;

            const res = await apiFetch(`${API_BASE}/usuarios/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const json: ApiResponse<Usuario> = await res.json();
            if (json.success) {
                setEditandoId(null);
                setMensaje({ tipo: 'ok', texto: 'Usuario actualizado correctamente.' });
                await cargar();
            } else {
                setMensaje({ tipo: 'error', texto: json.error || 'No se pudo actualizar el usuario.' });
            }
        } finally {
            setGuardandoId(null);
        }
    };

    const handleToggleActivo = async (u: Usuario) => {
        if (yo?.id === u.id) return;
        setGuardandoId(u.id);
        try {
            const res = await apiFetch(`${API_BASE}/usuarios/${u.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ activo: !u.activo })
            });
            const json: ApiResponse<Usuario> = await res.json();
            if (json.success) {
                setMensaje({ tipo: 'ok', texto: u.activo ? 'Usuario desactivado.' : 'Usuario reactivado.' });
                await cargar();
            } else {
                setMensaje({ tipo: 'error', texto: json.error || 'No se pudo cambiar el estado.' });
            }
        } finally {
            setGuardandoId(null);
        }
    };

    const handleResetearPassword = async (id: number) => {
        if (nuevaPassword.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }
        try {
            const res = await apiFetch(`${API_BASE}/usuarios/${id}/resetear-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password: nuevaPassword })
            });
            const json: ApiResponse<null> = await res.json();
            if (json.success) {
                toast.success('Contraseña actualizada');
                setResetearId(null);
                setNuevaPassword('');
            } else {
                toast.error(json.error || 'No se pudo resetear la contraseña');
            }
        } catch {
            toast.error('No se pudo conectar con el servidor');
        }
    };

    const togglePermiso = (lista: ModuloSistema[], modulo: ModuloSistema, activo: boolean): ModuloSistema[] =>
        activo ? [...new Set([...lista, modulo])] : lista.filter(m => m !== modulo);

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col transform transition-all"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white text-lg">
                            🧑‍💼
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">Usuarios</h2>
                            <p className="text-xs text-slate-500">Roles y módulos habilitados por usuario</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-5 overflow-y-auto flex-1 space-y-3">
                    {mensaje && (
                        <div className={`text-sm rounded-xl px-4 py-2.5 border ${
                            mensaje.tipo === 'ok'
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                : 'bg-red-50 border-red-200 text-red-700'
                        }`}>
                            {mensaje.texto}
                        </div>
                    )}

                    {!mostrarNuevo ? (
                        <button
                            onClick={() => { setMostrarNuevo(true); setMensaje(null); }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium border-2 border-dashed border-slate-200 text-slate-500 rounded-xl hover:border-blue-300 hover:text-blue-600 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Nuevo usuario
                        </button>
                    ) : (
                        <form onSubmit={handleCrear} className="bg-slate-50 rounded-xl p-4 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 mb-1">Nombre *</label>
                                    <input
                                        type="text"
                                        value={nuevoForm.nombre}
                                        onChange={(e) => setNuevoForm(prev => ({ ...prev, nombre: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
                                    <input
                                        type="email"
                                        value={nuevoForm.email}
                                        onChange={(e) => setNuevoForm(prev => ({ ...prev, email: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 mb-1">Contraseña * (mín. 6)</label>
                                    <input
                                        type="password"
                                        value={nuevoForm.password}
                                        onChange={(e) => setNuevoForm(prev => ({ ...prev, password: e.target.value }))}
                                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                        minLength={6}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 mb-1">Rol</label>
                                    <select
                                        value={nuevoForm.rol}
                                        onChange={(e) => setNuevoForm(prev => ({ ...prev, rol: e.target.value as RolUsuario }))}
                                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        {(['gerente', 'supervisor', 'operador'] as RolUsuario[]).map(r => (
                                            <option key={r} value={r}>{ROL_LABELS[r]}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1.5">Módulos habilitados</label>
                                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                                    {MODULOS_SISTEMA.map(m => (
                                        <ToggleModulo
                                            key={m}
                                            modulo={m}
                                            activo={nuevoForm.permisos_modulos.includes(m)}
                                            onChange={(v) => setNuevoForm(prev => ({
                                                ...prev,
                                                permisos_modulos: togglePermiso(prev.permisos_modulos, m, v)
                                            }))}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={() => { setMostrarNuevo(false); setNuevoForm(formVacio); }}
                                    className="px-3 py-2 text-xs font-medium border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={creando}
                                    className="px-3 py-2 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                >
                                    {creando ? 'Creando...' : 'Crear usuario'}
                                </button>
                            </div>
                        </form>
                    )}

                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-32 gap-3">
                            <div className="relative">
                                <div className="w-10 h-10 border-4 border-slate-200 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                            </div>
                            <p className="text-slate-500 text-sm">Cargando usuarios...</p>
                        </div>
                    ) : (
                        usuarios.map(u => (
                            <div
                                key={u.id}
                                className={`rounded-xl p-3 border ${u.activo ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-70'}`}
                            >
                                {editandoId === u.id ? (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-medium text-slate-700">{u.nombre} <span className="text-xs text-slate-500">({u.email})</span></p>
                                            {yo?.id !== u.id ? (
                                                <select
                                                    value={editRol}
                                                    onChange={(e) => setEditRol(e.target.value as RolUsuario)}
                                                    className="text-xs px-2 py-1 border border-slate-300 rounded-md bg-white"
                                                >
                                                    {(['gerente', 'supervisor', 'operador'] as RolUsuario[]).map(r => (
                                                        <option key={r} value={r}>{ROL_LABELS[r]}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="text-xs text-slate-500 italic">no podés cambiar tu propio rol</span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                                            {MODULOS_SISTEMA.map(m => (
                                                <ToggleModulo
                                                    key={m}
                                                    modulo={m}
                                                    activo={editPermisos.includes(m)}
                                                    onChange={(v) => setEditPermisos(prev => togglePermiso(prev, m, v))}
                                                />
                                            ))}
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => setEditandoId(null)}
                                                className="px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-100"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                onClick={() => handleGuardarEdicion(u.id)}
                                                disabled={guardandoId === u.id}
                                                className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                            >
                                                {guardandoId === u.id ? 'Guardando...' : 'Guardar'}
                                            </button>
                                        </div>
                                    </div>
                                ) : resetearId === u.id ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="password"
                                            value={nuevaPassword}
                                            onChange={(e) => setNuevaPassword(e.target.value)}
                                            placeholder="Nueva contraseña (mín. 6)"
                                            className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg"
                                            minLength={6}
                                            autoFocus
                                        />
                                        <button
                                            onClick={() => { setResetearId(null); setNuevaPassword(''); }}
                                            className="px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-100"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={() => handleResetearPassword(u.id)}
                                            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                        >
                                            Guardar
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        {/* Antes era un único flex-wrap con avatar+nombre+botones compitiendo
                                            por la misma línea — según el ancho de pantalla y la cantidad de
                                            botones, el algoritmo de flexbox terminaba o aplastando la columna
                                            del nombre a unos pocos px (letra por línea, ilegible) o desbordando
                                            los botones con scroll horizontal. Separar en "info" (avatar+nombre,
                                            siempre en fila) y "acciones" (botones), apilados en columna en
                                            pantallas angostas y en fila recién a partir de sm (640px, donde ya
                                            hay lugar real), evita la pelea por espacio en vez de intentar
                                            afinarla con anchos mínimos mágicos. */}
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                                {u.nombre.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?'}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-medium text-slate-700 truncate">
                                                        {u.nombre} {yo?.id === u.id && <span className="text-xs text-slate-500">(vos)</span>}
                                                    </p>
                                                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-indigo-100 text-indigo-700">
                                                        {ROL_LABELS[u.rol]}
                                                    </span>
                                                    {!u.activo && (
                                                        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-slate-200 text-slate-500">
                                                            Inactivo
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-500 truncate">{u.email}</p>
                                                <p className="text-[10px] text-slate-500 truncate">
                                                    {u.permisos_modulos.map(m => MODULO_LABELS[m]).join(' · ') || 'sin módulos habilitados'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap sm:flex-shrink-0">
                                            <button
                                                onClick={() => setResetearId(u.id)}
                                                className="px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                                            >
                                                🔑 Password
                                            </button>
                                            <button
                                                onClick={() => handleEditar(u)}
                                                className="px-3 py-1.5 text-xs font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                                            >
                                                Editar
                                            </button>
                                            <button
                                                onClick={() => handleToggleActivo(u)}
                                                disabled={yo?.id === u.id || guardandoId === u.id}
                                                title={yo?.id === u.id ? 'No podés desactivarte a vos mismo' : undefined}
                                                className={`px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors disabled:opacity-40 ${
                                                    u.activo
                                                        ? 'border-red-200 text-red-600 hover:bg-red-50'
                                                        : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
                                                }`}
                                            >
                                                {guardandoId === u.id ? '...' : u.activo ? 'Desactivar' : 'Reactivar'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                <div className="flex items-center justify-end p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors font-medium"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};
